import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  Upload,
  Button,
  Modal,
  message,
  Divider,
  Row,
  Col,
  Space,
  Input,
} from "antd";
import { CameraOutlined } from "@ant-design/icons";
import { Api_Url } from "../../../config/config";

const { Option } = Select;
const { TextArea } = Input;

export const BranchClosingModalAdd = ({ onClose, BranchClosingDataFunc }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [shutterImage, setShutterImage] = useState(null);
  const [cboApprovalImage, setCboApprovalImage] = useState(null);
  const [location, setLocation] = useState({ lat: null, lng: null });
  const [isAfter9PM, setIsAfter9PM] = useState(false);

  useEffect(() => {
    fetchBranches();
    getGeolocation();

    const now = new Date();
    const deadline = new Date();
    deadline.setHours(21, 0, 0, 0); // 9:00 PM

    // --- UPDATED LOGIC ---
    // This block now handles both cases: before and after 9 PM.
    if (now > deadline) {
      // If it's after 9 PM, default to "No" and disable the field.
      setIsAfter9PM(true);
      form.setFieldsValue({ IsClosedBy9PM: false });
    } else {
      // If it's before 9 PM, automatically select "Yes".
      form.setFieldsValue({ IsClosedBy9PM: true });
    }
    // --- END OF UPDATE ---
  }, [form]);

  const fetchBranches = async () => {
    try {
      const response = await fetch(
        `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user")).accessToken
            }`,
          },
        }
      );
      const data = await response.json();
      setBranches(data.data);
    } catch (error) {
      message.error("Failed to load branches");
    }
  };

  const getGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          message.warning(
            "Geolocation permission denied. Location will not be recorded."
          );
        }
      );
    }
  };

  const handleShutterImageUpload = (info) => {
    if (info.file.status === "removed") {
      setShutterImage(null);
      return;
    }
    const file = info.file.originFileObj || info.file;
    setShutterImage(file);
  };

  const handleCboApprovalImageUpload = (info) => {
    if (info.file.status === "removed") {
      setCboApprovalImage(null);
      return;
    }
    const file = info.file.originFileObj || info.file;
    setCboApprovalImage(file);
  };

  const onFinish = async (values) => {
    if (!shutterImage) {
      message.error("Please upload the shutter/main door image");
      return;
    }
    
    if (values.IsClosedBy9PM === false) {
      if (values.HasCBOApproval === null || values.HasCBOApproval === undefined) {
        message.error("Please specify if you received CBO approval.");
        return;
      }
      if (!values.ReasonForDelay) {
        message.error("Please provide a reason for the delay.");
        return;
      }
      if (values.HasCBOApproval === true && !cboApprovalImage) {
        message.error("Please upload the CBO approval image.");
        return;
      }
    }

    setLoading(true);
    const formData = new FormData();

    // Append all form data
    formData.append("BranchId", values.BranchId);
    formData.append("ShutterImage", shutterImage);
    formData.append("Latitude", location.lat || "");
    formData.append("Longitude", location.lng || "");

    if (values.IsClosedBy9PM !== undefined) {
      formData.append("IsClosedBy9PM", values.IsClosedBy9PM);
    }

    if (values.IsClosedBy9PM === false) {
      formData.append("ReasonForDelay", values.ReasonForDelay || "");
      formData.append("HasCBOApproval", values.HasCBOApproval);
      if (values.HasCBOApproval === true && cboApprovalImage) {
        formData.append("CBOApprovalImage", cboApprovalImage);
      }
    }

    try {
      const response = await fetch(`${Api_Url}api/v1/BranchClosing`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user")).accessToken
          }`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Submission failed");
      }

      message.success("Branch closing report submitted successfully");
      if (BranchClosingDataFunc) BranchClosingDataFunc();
      onClose();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: "20px", fontWeight: "bold" }}>
          Add Branch Closing Report
        </span>
      }
      width={700}
      visible={true}
      onCancel={onClose}
      footer={[
        <Space key="footer-buttons" size="large">
          <Button key="back" onClick={onClose} size="large" style={{ width: 120 }}>
            Cancel
          </Button>
          <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()} size="large" style={{ width: 120 }}>
            Submit
          </Button>
        </Space>,
      ]}
      centered
      bodyStyle={{ padding: "24px" }}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="BranchId"
              label={<span style={{ fontWeight: 500 }}>Branch</span>}
              rules={[{ required: true, message: "Please select a branch" }]}
            >
              <Select
                placeholder="Select branch"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                size="large"
              >
                {branches.map((branch) => (
                  <Option key={branch.id} value={branch.id}>
                    {branch.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="IsClosedBy9PM"
              label={
                <span style={{ fontWeight: 500 }}>Branch Closed by 9 PM?</span>
              }
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Select
                placeholder="Select Yes or No"
                size="large"
                disabled={true}
              >
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.IsClosedBy9PM !== currentValues.IsClosedBy9PM
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("IsClosedBy9PM") === false && (
              <>
                <Divider orientation="left">Late Closing Details</Divider>
                <Row gutter={24}>
                  <Col span={24}>
                    <Form.Item
                      name="HasCBOApproval"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          CBO Approval Received?
                        </span>
                      }
                      rules={[
                        { required: true, message: "This field is required" },
                      ]}
                    >
                      <Select placeholder="Select Yes or No" size="large">
                        <Option value={true}>Yes</Option>
                        <Option value={false}>No</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={24}>
                  <Col span={24}>
                    <Form.Item
                      name="ReasonForDelay"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Reason for Delay / Remarks
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please enter the reason for the delay",
                        },
                      ]}
                    >
                      <TextArea
                        rows={3}
                        placeholder="Explain why the branch was not closed on time"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.HasCBOApproval !== currentValues.HasCBOApproval
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue("HasCBOApproval") === true && (
                      <Row gutter={24}>
                        <Col span={24}>
                          <Form.Item
                            label={
                              <span style={{ fontWeight: 500 }}>
                                Upload CBO Approval
                              </span>
                            }
                            required
                          >
                            <Upload
                              accept="image/*"
                              listType="picture-card"
                              showUploadList={false}
                              beforeUpload={() => false}
                              onChange={handleCboApprovalImageUpload}
                            >
                              {cboApprovalImage ? (
                                <img
                                  src={URL.createObjectURL(cboApprovalImage)}
                                  alt="approval"
                                  style={{ width: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <div>
                                  <CameraOutlined />
                                  <div style={{ marginTop: 8 }}>Upload Image</div>
                                </div>
                              )}
                            </Upload>
                          </Form.Item>
                        </Col>
                      </Row>
                    )
                  }
                </Form.Item>
              </>
            )
          }
        </Form.Item>

        <Divider orientation="left">Documentation</Divider>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              label={
                <span style={{ fontWeight: 500 }}>
                  Shutter/Main Door Image
                </span>
              }
              required
            >
              <Upload
                accept="image/*"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleShutterImageUpload}
              >
                {shutterImage ? (
                  <img
                    src={URL.createObjectURL(shutterImage)}
                    alt="shutter"
                    style={{ width: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div>
                    <CameraOutlined />
                    <div style={{ marginTop: 8 }}>Upload Image</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};